import moment from 'moment'
import {IntlShape} from 'react-intl'
import {DEFAULT_PAGE, DEFAULT_PAGE_SIZE} from '../../../../_gori/constants'
import {COMPARISON_OPERATORS, ORGANIZATION_ACCOUNTS_FILTER_QUERY_KEYS} from '../core/_const'
import {
  ComparisonOperator,
  OrganizationAccountsFilterValues,
} from '../modals/OrganizationAccountsFilter'
import {OrganizationManager} from '../core/_types'

export type ActiveFilterChipId =
  | 'accountCreationDate'
  | 'lastShipmentDate'
  | 'totalSpend'
  | 'totalVolume'

export type ActiveFilterChip = {
  id: ActiveFilterChipId
  label: string
  value: string
}

const OPERATOR_SYMBOL: Record<ComparisonOperator, string> = {
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  neq: '≠',
  between: 'between',
  not_between: 'not between',
}

export const isRangeComparisonOperator = (operator: ComparisonOperator) =>
  operator === 'between' || operator === 'not_between'

export const isActiveComparisonFilter = (
  operator: ComparisonOperator,
  value: string,
  valueTo: string
): boolean => {
  const hasValue = value.trim() !== ''
  const hasValueTo = valueTo.trim() !== ''

  if (isRangeComparisonOperator(operator)) {
    return hasValue && hasValueTo
  }

  return hasValue
}

export const mapManagersToAssignOptions = (managers: OrganizationManager[] = []) =>
  managers.map((manager) => {
    return {
      value: String(manager.branch_id),
      label: manager.branch_name,
    }
  })

const formatCurrencyValue = (value: string): string => {
  const num = Number(value.replace(/[^0-9.]/g, ''))
  if (Number.isNaN(num)) {
    return value
  }
  if (num >= 1000) {
    return `$${num / 1000}K`
  }
  return `$${num.toLocaleString()}`
}

const formatVolumeValue = (value: string): string => {
  const num = Number(value.replace(/[^0-9.]/g, ''))
  if (Number.isNaN(num)) {
    return value
  }
  return num.toLocaleString()
}

const getDateRangeDisplay = (
  from: string | null,
  to: string | null,
  intl: IntlShape
): string | null => {
  if (!from || !to) {
    return null
  }

  const start = moment(from).startOf('day')
  const end = moment(to).endOf('day')
  const lastMonthStart = moment().subtract(1, 'month').startOf('month')
  const lastMonthEnd = moment().subtract(1, 'month').endOf('month')

  if (start.isSame(lastMonthStart, 'day') && end.isSame(lastMonthEnd, 'day')) {
    return intl.formatMessage({id: 'LAST_MONTH'})
  }

  const last7Start = moment().subtract(6, 'days').startOf('day')
  if (start.isSame(last7Start, 'day') && end.isSame(moment().endOf('day'), 'day')) {
    return intl.formatMessage({id: 'LAST_7_DAYS'})
  }

  const dateFormat = process.env.REACT_APP_DATE_FORMAT ?? 'MM/DD/YYYY'
  return `${start.format(dateFormat)} - ${end.format(dateFormat)}`
}

const formatComparisonDisplay = (
  operator: ComparisonOperator,
  value: string,
  valueTo: string,
  isCurrency: boolean
): string => {
  const formattedValue = isCurrency ? formatCurrencyValue(value) : formatVolumeValue(value)

  if (isRangeComparisonOperator(operator) && valueTo.trim()) {
    const formattedValueTo = isCurrency ? formatCurrencyValue(valueTo) : formatVolumeValue(valueTo)
    return `${OPERATOR_SYMBOL[operator]} ${formattedValue} - ${formattedValueTo}`
  }

  return `${OPERATOR_SYMBOL[operator]} ${formattedValue}`
}

export const buildActiveFilterChips = (
  filters: OrganizationAccountsFilterValues,
  intl: IntlShape
): ActiveFilterChip[] => {
  const chips: ActiveFilterChip[] = []

  const accountCreationDisplay = getDateRangeDisplay(
    filters.accountCreationDateFrom,
    filters.accountCreationDateTo,
    intl
  )
  if (accountCreationDisplay) {
    chips.push({
      id: 'accountCreationDate',
      label: intl.formatMessage({id: 'ACCOUNT_CREATION_DATE'}),
      value: accountCreationDisplay,
    })
  }

  const lastShipmentDisplay = getDateRangeDisplay(
    filters.lastShipmentDateFrom,
    filters.lastShipmentDateTo,
    intl
  )
  if (lastShipmentDisplay) {
    chips.push({
      id: 'lastShipmentDate',
      label: intl.formatMessage({id: 'LAST_SHIPMENT_DATE'}),
      value: lastShipmentDisplay,
    })
  }

  if (
    isActiveComparisonFilter(
      filters.totalSpendOperator,
      filters.totalSpendValue,
      filters.totalSpendValueTo
    )
  ) {
    chips.push({
      id: 'totalSpend',
      label: intl.formatMessage({id: 'TOTAL_SHIPMENT_SPEND'}),
      value: formatComparisonDisplay(
        filters.totalSpendOperator,
        filters.totalSpendValue,
        filters.totalSpendValueTo,
        true
      ),
    })
  }

  if (
    isActiveComparisonFilter(
      filters.totalVolumeOperator,
      filters.totalVolumeValue,
      filters.totalVolumeValueTo
    )
  ) {
    chips.push({
      id: 'totalVolume',
      label: intl.formatMessage({id: 'TOTAL_SHIPMENT_VOLUME'}),
      value: formatComparisonDisplay(
        filters.totalVolumeOperator,
        filters.totalVolumeValue,
        filters.totalVolumeValueTo,
        false
      ),
    })
  }

  return chips
}

export const removeFilterChip = (
  filters: OrganizationAccountsFilterValues,
  chipId: ActiveFilterChipId
): OrganizationAccountsFilterValues => {
  switch (chipId) {
    case 'accountCreationDate':
      return {...filters, accountCreationDateFrom: null, accountCreationDateTo: null}
    case 'lastShipmentDate':
      return {...filters, lastShipmentDateFrom: null, lastShipmentDateTo: null}
    case 'totalSpend':
      return {
        ...filters,
        totalSpendValue: '',
        totalSpendValueTo: '',
        totalSpendOperator: 'gt',
      }
    case 'totalVolume':
      return {
        ...filters,
        totalVolumeValue: '',
        totalVolumeValueTo: '',
        totalVolumeOperator: 'gt',
      }
    default:
      return filters
  }
}

export const hasActiveAccountFilters = (
  filters: OrganizationAccountsFilterValues | null
): boolean => {
  if (!filters) {
    return false
  }

  return Boolean(
    filters.accountCreationDateFrom ||
      filters.lastShipmentDateFrom ||
      isActiveComparisonFilter(
        filters.totalSpendOperator,
        filters.totalSpendValue,
        filters.totalSpendValueTo
      ) ||
      isActiveComparisonFilter(
        filters.totalVolumeOperator,
        filters.totalVolumeValue,
        filters.totalVolumeValueTo
      )
  )
}

const isComparisonOperator = (value: string | null): value is ComparisonOperator =>
  !!value && COMPARISON_OPERATORS.some((operator) => operator === value)

const setSearchParam = (searchParams: URLSearchParams, key: string, value: string | null) => {
  if (value) {
    searchParams.set(key, value)
    return
  }

  searchParams.delete(key)
}

export const parseOrganizationAccountsFiltersFromSearchParams = (
  searchParams: URLSearchParams
): OrganizationAccountsFilterValues | null => {
  const filters: OrganizationAccountsFilterValues = {
    accountCreationDateFrom: searchParams.get('create_date_from'),
    accountCreationDateTo: searchParams.get('create_date_to'),
    lastShipmentDateFrom: searchParams.get('last_shipment_date_from'),
    lastShipmentDateTo: searchParams.get('last_shipment_date_to'),
    totalSpendOperator: isComparisonOperator(searchParams.get('spend_operator'))
      ? (searchParams.get('spend_operator') as ComparisonOperator)
      : 'gt',
    totalSpendValue: searchParams.get('spend_value') ?? '',
    totalSpendValueTo: searchParams.get('spend_value_to') ?? '',
    totalVolumeOperator: isComparisonOperator(searchParams.get('volume_operator'))
      ? (searchParams.get('volume_operator') as ComparisonOperator)
      : 'gt',
    totalVolumeValue: searchParams.get('volume_value') ?? '',
    totalVolumeValueTo: searchParams.get('volume_value_to') ?? '',
  }

  return hasActiveAccountFilters(filters) ? filters : null
}

export const applyOrganizationAccountsFiltersToSearchParams = (
  searchParams: URLSearchParams,
  filters: OrganizationAccountsFilterValues | null
): URLSearchParams => {
  const next = new URLSearchParams(searchParams)

  ORGANIZATION_ACCOUNTS_FILTER_QUERY_KEYS.forEach((key) => next.delete(key))

  if (!filters || !hasActiveAccountFilters(filters)) {
    return next
  }

  setSearchParam(next, 'create_date_from', filters.accountCreationDateFrom)
  setSearchParam(next, 'create_date_to', filters.accountCreationDateTo)
  setSearchParam(next, 'last_shipment_date_from', filters.lastShipmentDateFrom)
  setSearchParam(next, 'last_shipment_date_to', filters.lastShipmentDateTo)

  const hasActiveSpend = isActiveComparisonFilter(
    filters.totalSpendOperator,
    filters.totalSpendValue,
    filters.totalSpendValueTo
  )
  if (hasActiveSpend) {
    setSearchParam(next, 'spend_operator', filters.totalSpendOperator)
    setSearchParam(next, 'spend_value', filters.totalSpendValue.trim() || null)
    if (isRangeComparisonOperator(filters.totalSpendOperator)) {
      setSearchParam(next, 'spend_value_to', filters.totalSpendValueTo.trim() || null)
    }
  }

  const hasActiveVolume = isActiveComparisonFilter(
    filters.totalVolumeOperator,
    filters.totalVolumeValue,
    filters.totalVolumeValueTo
  )
  if (hasActiveVolume) {
    setSearchParam(next, 'volume_operator', filters.totalVolumeOperator)
    setSearchParam(next, 'volume_value', filters.totalVolumeValue.trim() || null)
    if (isRangeComparisonOperator(filters.totalVolumeOperator)) {
      setSearchParam(next, 'volume_value_to', filters.totalVolumeValueTo.trim() || null)
    }
  }

  return next
}

export const serializeOrganizationAccountsFilters = (
  filters: OrganizationAccountsFilterValues | null
) => (filters ? JSON.stringify(filters) : '')

export const buildOrganizationAccountsParams = (
  searchParams: URLSearchParams,
  accountIds: Array<string | number> = []
) => {
  const filters = parseOrganizationAccountsFiltersFromSearchParams(searchParams)
  const hasActiveSpend =
    !!filters &&
    isActiveComparisonFilter(
      filters.totalSpendOperator,
      filters.totalSpendValue,
      filters.totalSpendValueTo
    )
  const hasActiveVolume =
    !!filters &&
    isActiveComparisonFilter(
      filters.totalVolumeOperator,
      filters.totalVolumeValue,
      filters.totalVolumeValueTo
    )

  return {
    account_ids: accountIds,
    page: searchParams.get('page') || DEFAULT_PAGE,
    page_size: searchParams.get('page_size') || DEFAULT_PAGE_SIZE,
    create_date_from: filters?.accountCreationDateFrom || null,
    create_date_to: filters?.accountCreationDateTo || null,
    last_shipment_date_from: filters?.lastShipmentDateFrom || null,
    last_shipment_date_to: filters?.lastShipmentDateTo || null,
    spend_operator: hasActiveSpend ? filters.totalSpendOperator : null,
    spend_value: hasActiveSpend ? filters.totalSpendValue.trim() || null : null,
    spend_value_to:
      hasActiveSpend && isRangeComparisonOperator(filters.totalSpendOperator)
        ? filters.totalSpendValueTo.trim() || null
        : null,
    volume_operator: hasActiveVolume ? filters.totalVolumeOperator : null,
    volume_value: hasActiveVolume ? filters.totalVolumeValue.trim() || null : null,
    volume_value_to:
      hasActiveVolume && isRangeComparisonOperator(filters.totalVolumeOperator)
        ? filters.totalVolumeValueTo.trim() || null
        : null,
  }
}
